'use client'

import React from 'react'
import type { CampAnmeldung, Teilnehmer, Camp } from '@/payload-types'
import { ArrowRight, Calendar, MapPin, Users, Euro, Tag } from 'lucide-react'

type PopulatedAnmeldung = CampAnmeldung & {
  teilnehmer: Teilnehmer
  camp: Camp
  alternativeCamp?: Camp | null
}

interface CampSelectionGridProps {
  camps: Camp[]
  selectedCamps: string[]
  onCampToggle: (campId: string) => void
  groupedAnmeldungen: Record<
    string,
    {
      camp: Camp
      anmeldungen: PopulatedAnmeldung[]
      totalRegistrations?: number
      openRegistrations?: number
      statusBreakdown?: Record<string, number>
    }
  >
  emailSearch?: string
}

const CampSelectionGrid = ({
  camps,
  selectedCamps,
  onCampToggle,
  groupedAnmeldungen,
  emailSearch,
}: CampSelectionGridProps) => {
  const handleCampClick = (campId: string) => {
    // Navigate to the detailed view for this camp
    const url = new URL(window.location.href)
    url.searchParams.set('camp', campId)
    url.searchParams.set('view', 'details')

    // Pass emailSearch parameter if it exists
    if (emailSearch && emailSearch.trim()) {
      url.searchParams.set('emailSearch', emailSearch.trim())
    }

    window.location.href = url.toString()
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="mb-6 text-lg font-semibold">Wähle ein Camp aus</h3>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {camps.map((camp) => {
          const campData = groupedAnmeldungen[camp.id]
          const totalCount = campData?.totalRegistrations || 0
          const openCount = campData?.openRegistrations || 0
          const isPaidCamp = camp.isPaidCamp

          return (
            <div
              key={camp.id}
              onClick={() => handleCampClick(camp.id)}
              className="group relative cursor-pointer rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-blue-300 hover:shadow-md"
            >
              {/* Arrow indicator */}
              <div className="absolute right-4 top-4 opacity-0 transition-opacity group-hover:opacity-100">
                <ArrowRight className="h-5 w-5 text-blue-500" />
              </div>

              {/* Camp Info */}
              <div className="pr-8">
                <div className="mb-3 flex items-center gap-3">
                  <h4 className="text-lg font-semibold text-gray-900 transition-colors group-hover:text-blue-700">
                    {camp.title || 'Unnamed Camp'}
                  </h4>

                  {/* SAACnd Step Camp Badge */}
                  {isPaidCamp && (
                    <div className="flex items-center gap-1 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 px-2 py-1 text-xs font-semibold text-white shadow-sm">
                      SAACnd Step
                    </div>
                  )}
                </div>

                <div className="mb-4 space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="mr-2 h-4 w-4" />
                    {camp.location || 'Location not specified'}
                  </div>

                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="mr-2 h-4 w-4" />
                    {camp.date ? new Date(camp.date).toLocaleDateString('de-DE') : 'Date not set'}
                  </div>

                  <div className="flex items-center text-sm text-gray-600">
                    <Tag className="mr-2 h-4 w-4" />
                    { typeof camp.category === 'object' ? camp.category.name : camp.category }
                  </div>

                  {/* Price Display */}
                  {isPaidCamp && camp.price && (
                    <div className="flex items-center text-sm font-medium text-blue-700">
                      <Euro className="mr-2 h-4 w-4" />
                      {camp.price}€
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-600">
                    <Users className="mr-2 h-4 w-4" />
                    {totalCount} Anmeldungen
                  </div>
                  {openCount > 0 && (
                    <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
                      {openCount} offen
                    </span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default CampSelectionGrid
